import * as React from "react";
import { Text, View } from "react-native";
import { Image } from "expo-image";
import {
  Link,
  Redirect,
  Stack,
  useGlobalSearchParams,
  useRouter,
} from "expo-router";
import { decodeEntity } from "html-entities";
import { CameraIcon } from "lucide-react-native";

import { AvatarCropper, pickAvatar } from "~/components/AvatarEditor";
import { useHonoClient } from "~/hooks/HonoContext";
import { useGlobalStore } from "~/stores/global";
import { Button, ButtonProps } from "~/ui/Button";

export default function ProfilePicturePage() {
  const { refreshStore, store } = useGlobalStore(true);
  const { replace } = useRouter();
  const { next } = useGlobalSearchParams();

  if (!store.personalAccount?.id) return <Redirect href="/create-account" />;

  const { hc } = useHonoClient();
  const [image, setImage] = React.useState<string | undefined>(undefined);
  const [isSavingAvatar, setIsSavingAvatar] = React.useState(false);

  const nextUrl = next ? (next as `/${string}`) : "/";

  const saveAvatar = async () => {
    setIsSavingAvatar(true);
    const response = await hc.accounts[":accountId"].$patch({
      param: { accountId: store.personalAccount!.id },
      json: { avatar: image },
    });
    if (!response.ok) {
      console.error("Failed to save avatar", response);
      setIsSavingAvatar(false);
    } else await refreshStore().then(() => replace(nextUrl));
  };

  return (
    <>
      <Stack.Screen
        options={{
          headerTitle: "Add your profile picture",
          headerBackTitleVisible: false,
        }}
      />
      <View className="bg-grey-dark-0 flex-grow items-center  px-6 py-4">
        <View className="max-w-96 gap-y-8">
          <View className="items-center gap-y-8">
            <Text className="text-text-secondary">
              Add a profile photo so that your friends know it
              {decodeEntity("&apos;")}s you.
            </Text>
            {!image ? (
              <View className="h-32 w-32 items-center justify-center rounded-full border border-dashed border-white text-white">
                <Text className="text-text-primary">
                  <CameraIcon size={48} />
                </Text>
              </View>
            ) : (
              <View className="gap-2">
                <View className="relative h-32 w-32 items-center justify-center overflow-hidden rounded-full">
                  <Image
                    source={{
                      uri: image,
                    }}
                    contentFit="cover"
                    className="rounded-full"
                    style={{ width: 128, height: 128 }}
                  />
                </View>
                <AvatarPicker
                  onAvatarChange={setImage}
                  variant="secondary"
                  size="small"
                >
                  Change
                </AvatarPicker>
              </View>
            )}
          </View>
          <View className="gap-4">
            {image ? (
              <Button
                variant="primary"
                onPress={saveAvatar}
                isLoading={isSavingAvatar}
              >
                Save and continue
              </Button>
            ) : (
              <AvatarPicker onAvatarChange={setImage}>
                Select image
              </AvatarPicker>
            )}
            <Button variant="secondary" asChild>
              <Link href={nextUrl} className="text-button-label">
                Skip for now
              </Link>
            </Button>
          </View>
        </View>
      </View>
    </>
  );
}

function AvatarPicker(
  props: { onAvatarChange: (image: string) => void } & ButtonProps,
) {
  const { onAvatarChange, ...rest } = props;
  const [state, setState] = React.useState<"init" | "crop">("init");
  const [image, setImage] = React.useState<string | undefined>(undefined);

  return (
    <View>
      <Button
        onPress={() =>
          pickAvatar().then((image) => {
            if (!image) return;
            else {
              setImage(image);
              setState("crop");
            }
          })
        }
        {...rest}
      />
      {state === "crop" && image && (
        <AvatarCropper
          image={image}
          onCancel={() => setState("init")}
          onSave={(image) => {
            setState("init");
            setImage(undefined);
            onAvatarChange(image);
          }}
        />
      )}
    </View>
  );
}
